import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Trophy, AlertTriangle, ArrowRight } from 'lucide-react';
import { fmtPct, fmtMoney, fmtAum, fmtDate } from './ccFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Live head-to-head panel for embedding inside Learn guides.
 *
 * Same data and math as the /{a}-vs-{b} pages: verdict, five live
 * metrics with winner badges, the last 12 distributions charted, and
 * the risk flags. Keeps guide prose evergreen — no hand-edited yields.
 */
export default function LiveVersusPanel({ slug, hubTo = '/covered-call-etfs' }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/covered-call/compare/${slug}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [slug]);

  if (failed) return null;

  if (!data) {
    return (
      <div className="not-prose my-6 h-64 rounded-xl bg-slate-100 dark:bg-slate-800/60 animate-pulse" data-testid="live-vs-loading" />
    );
  }

  const { a, b, comparison: cmp } = data;
  const winner = cmp.overall_winner === 'a' ? a : cmp.overall_winner === 'b' ? b : null;
  const loser = cmp.overall_winner === 'a' ? b : cmp.overall_winner === 'b' ? a : null;

  return (
    <div className="not-prose my-8 rounded-2xl border-2 border-cyan-300 dark:border-cyan-800 overflow-hidden" data-testid={`live-vs-${slug}`}>
      <div className="px-5 py-4 bg-gradient-to-br from-cyan-50 to-emerald-50/60 dark:from-slate-900 dark:to-slate-900/40 border-b border-cyan-200 dark:border-cyan-900">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 dark:text-cyan-400">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
          </span>
          Live data · updated nightly
        </div>
        {winner ? (
          <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <Trophy className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
            <span className="text-2xl font-black text-slate-900 dark:text-slate-100" data-testid="live-vs-winner">
              {winner.total_return_1y < 0 ? `${winner.ticker} lost less` : `${winner.ticker} is ahead`}
            </span>
            <span className={`text-base font-bold tabular-nums ${winner.total_return_1y < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
              {fmtPct(winner.total_return_1y, 1, true)}
            </span>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              total return vs {fmtPct(loser?.total_return_1y, 1, true)} for {loser?.ticker}
            </span>
          </div>
        ) : (
          <div className="mt-2 text-xl font-black text-slate-900 dark:text-slate-100" data-testid="live-vs-winner">
            {a.ticker} vs {b.ticker}
          </div>
        )}
        <p className="mt-2 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{cmp.verdict}</p>
      </div>

      <table className="w-full text-sm" data-testid="live-vs-table">
        <thead className="bg-slate-50 dark:bg-slate-900/60">
          <tr>
            <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">Live metric</th>
            <th className="px-4 py-2 text-right text-xs font-mono font-bold text-slate-900 dark:text-slate-100">{a.ticker}</th>
            <th className="px-4 py-2 text-right text-xs font-mono font-bold text-slate-900 dark:text-slate-100">{b.ticker}</th>
          </tr>
        </thead>
        <tbody>
          <Row label="1-year total return" a={fmtPct(a.total_return_1y, 1, true)} b={fmtPct(b.total_return_1y, 1, true)} win={cmp.winners.total_return_1y} badge="win" emphasis testId="tr" />
          <Row label="Distribution yield" a={fmtPct(a.dividend_yield)} b={fmtPct(b.dividend_yield)} win={cmp.winners.dividend_yield} badge="higher" testId="yield" />
          <Row label="Paid per share (12mo)" a={fmtMoney(a.dist_ttm)} b={fmtMoney(b.dist_ttm)} win={cmp.winners.dist_ttm} badge="higher" testId="dist" />
          <Row label="Share price change (1y)" a={fmtPct(a.nav_change_1y, 1, true)} b={fmtPct(b.nav_change_1y, 1, true)} win={cmp.winners.nav_change_1y} badge="better" testId="nav" />
          <Row label="Expense ratio" a={fmtPct(a.expense_ratio, 2)} b={fmtPct(b.expense_ratio, 2)} win={cmp.winners.expense_ratio} badge="cheaper" testId="fee" />
          <Row label="Fund size" a={fmtAum(a.net_assets)} b={fmtAum(b.net_assets)} win={cmp.winners.net_assets} badge="bigger" testId="aum" />
          <Row label="Pays" a={a.frequency || '—'} b={b.frequency || '—'} testId="freq" />
        </tbody>
      </table>

      <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-800">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
          Last 12 distributions per share
        </div>
        <div className="mt-3 grid sm:grid-cols-2 gap-4">
          {[a, b].map((f) => <MiniHistory key={f.ticker} fund={f} />)}
        </div>
      </div>

      {!!cmp.flags?.length && (
        <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-800 bg-amber-50/60 dark:bg-amber-950/20 space-y-1.5" data-testid="live-vs-flags">
          {cmp.flags.map((f, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-300">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{f.text}</span>
            </div>
          ))}
        </div>
      )}

      <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] text-slate-500 dark:text-slate-400">
          Prices as of {fmtDate(a.as_of || b.as_of)} · distributions through{' '}
          {fmtDate((a.history?.slice(-1)[0] || {}).date || a.last_dividend_date)}
        </span>
        <Link to={hubTo} className="inline-flex items-center gap-1 text-xs font-bold text-cyan-700 dark:text-cyan-400 hover:underline" data-testid="live-vs-hub-link">
          Compare all 190 option-income ETFs <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

function Row({ label, a, b, win, badge = 'win', emphasis, testId }) {
  const cell = (side, value) => {
    const isWin = win === side;
    return (
      <td className={`px-4 py-2 text-right tabular-nums whitespace-nowrap ${isWin ? 'font-bold text-slate-900 dark:text-slate-100' : 'text-slate-700 dark:text-slate-300'}`}>
        {value}{isWin && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-cyan-600 dark:text-cyan-400">{badge}</span>}
      </td>
    );
  };
  return (
    <tr className={`border-t border-slate-100 dark:border-slate-800/70 ${emphasis ? 'bg-slate-50/80 dark:bg-slate-900/40' : ''}`} data-testid={`live-vs-metric-${testId}`}>
      <td className={`px-4 py-2 text-left ${emphasis ? 'font-bold text-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-400'}`}>{label}</td>
      {cell('a', a)}
      {cell('b', b)}
    </tr>
  );
}

function MiniHistory({ fund }) {
  const rows = (fund.history || []).slice(-12);
  const max = rows.reduce((m, r) => Math.max(m, r.amount), 0) || 1;
  if (!rows.length) return null;
  return (
    <div data-testid={`live-vs-history-${fund.ticker}`}>
      <div className="flex items-baseline justify-between text-xs">
        <span className="font-mono font-bold text-slate-900 dark:text-slate-100">{fund.ticker}</span>
        <span className="text-slate-500 dark:text-slate-400">{fmtMoney(fund.dist_ttm)} over 12mo</span>
      </div>
      <div className="mt-2 flex items-end gap-1 h-16">
        {rows.map((r) => (
          <div key={r.date} className="flex-1 flex flex-col justify-end h-full" title={`${r.date}: ${fmtMoney(r.amount, 4)}`}>
            <div className="w-full rounded-t bg-cyan-500/80 hover:bg-cyan-400 transition-colors" style={{ height: `${Math.max(4, (r.amount / max) * 100)}%` }} />
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-slate-500 dark:text-slate-400">
        <span>{fmtDate(rows[0].date)}</span>
        <span>{fmtDate(rows[rows.length - 1].date)}</span>
      </div>
    </div>
  );
}
